import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { getLocations, logLogin, getUserAttributes } from '../../utils/AWSAPI';
import '../../App.css';
import '../../assets/css/Login.css';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../../utils/LoadingSpinner';
import { QRCodeSVG } from 'qrcode.react';

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setLoading] = useState(false);

  const [mfaStep, setMfaStep] = useState("none");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaUser, setMfaUser] = useState(null);
  const [totpSecret, setTotpSecret] = useState("");
  const [totpUri, setTotpUri] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const loginInProgress = useRef(false);
  const hasCheckedAuth = useRef(false);

  const location = useLocation();

  const onChangeUsername = (event) => {
    setError("");
    setMessage("");
    setUsername(event.target.value);
  }

  const onChangePassword = (event) => {
    setError("");
    setMessage("");
    setPassword(event.target.value);
  }

  function fixEmail(email) {
    if (!email) return '';
    return email.toString().includes("_") ? email.split('_')[1] : email;
  }

  function isStoreAccount(email) {
     return /^\d+$/.test(email.split('@')[0]);
  }
  
  const handleLoginSuccess = useCallback(async (user) => {
    if (loginInProgress.current) {
      console.log("Login already in progress, skipping...");
      return;
    }

    loginInProgress.current = true;
    setLoading(true);
    console.log("Starting login handle for:", user);

    let userAttributes = {};
    let userIdentifier = '';
    let authMethod = 'SSO';

    try {
      console.log("Getting user attributes from API...");
      const attributeResponse = await getUserAttributes(user.username);
      
      if (attributeResponse.success) {
        userAttributes = attributeResponse.data;
      } else {
        console.error("API returned error:", attributeResponse.message);
        setError("Failed to retrieve user information. Please try again.");
        setLoading(false);
        loginInProgress.current = false;
        return;
      }
    } catch (error) {
      console.error("API call failed:", error);
      setError("Failed to retrieve user information. Please try again.");
      setLoading(false);
      loginInProgress.current = false;
      return;
    }

    console.log("Final user attributes:", userAttributes)
    
    const email = userAttributes.email;

    if(email && email.includes("@fujitsu.com")) {
      console.log("Using local account. Identifier:", email);
      userIdentifier = email;
      authMethod = "Local";
    } else {
      userIdentifier = userAttributes.EID || email || user.username;
    }

    let locationData;

    try {
      // First attempt with the original user identifier (could be either email or EID)
      locationData = await getLocations("False", userIdentifier, "0", "1");
      
      // If no location found, try adding @fujitsu.com
      if (!locationData[0]) {
        const newEmail = userIdentifier.split('@')[0] + '@fujitsu.com';
        locationData = await getLocations("False", newEmail, "0", "1");

        if (!locationData[0]) {
          setError("Warning: Your account doesn't seem to have any associated restaurants. Please contact your administrator.");
          setLoading(false);
          return loginInProgress.current = false;
        } else {
          userIdentifier = newEmail;
        }
      }

      // Log the login with the authentication method
      console.log("User Identifier: " + userIdentifier);
      //await logLogin(userIdentifier, authMethod);

      // Set local storage
      localStorage.setItem("user_identifier", userIdentifier);

      localStorage.setItem("user_username", user.username.toString());
      localStorage.setItem("user_email", userAttributes.email);
      localStorage.setItem("user_eid", userAttributes.EID);

      localStorage.setItem("user_store_account", isStoreAccount(userIdentifier));
      localStorage.setItem("user_auth_method", authMethod);
      
      setLoading(false);
      setMessage("Successfully logged in. Welcome!");
      
      // Redirect based on user type (power users have a search bar due to store amount)
      if(locationData[0]) {
        localStorage.setItem("user_power_user", locationData[0].ispowerUser);
        window.location.href = locationData[0].ispowerUser ? "/home" : "/restaurants";
      } else {
        window.location.href = "/home";
      }
      
    } catch (error) {
      console.error("Error handling login success:", error);
      setError("An error occurred while processing your login. Please try again.");
      setLoading(false);
      loginInProgress.current = false;
    }
  }, []);

  const handleAuthCode = useCallback(async () => {
    try {
      await Auth.federatedSignIn();
      const user = await Auth.currentAuthenticatedUser();
      await handleLoginSuccess(user);
    } catch (error) {
      console.error('Error handling SSO authentication:', error);
      setError('Failed to complete authentication. Please try again.');
    }
  }, [handleLoginSuccess]);

  useEffect(() => {
    if (hasCheckedAuth.current) {
      console.log("Auth already checked, skipping...");
      return;
    }

    if (location.state?.error) {
      setError(location.state.error);
    }

    const checkAuthStatus = async () => {
      try {
        const session = await Auth.currentSession();
        if (session.isValid()) {
          const user = await Auth.currentAuthenticatedUser();
          await handleLoginSuccess(user);
        }
      } catch (error) {
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        if (authCode) {
          handleAuthCode();
        }
      } finally {
        hasCheckedAuth.current = true;
      }
    };

    checkAuthStatus();
  }, [handleLoginSuccess, handleAuthCode, location.state?.error]);
  
  const onClickLogin = async () => {
    if (loginInProgress.current) {
      console.log("Login already in progress...");
      return;
    }

    try {
      if (!username) {
        setError("Please Enter Username");
        return false;
      } else if(!password) {
        setError("Please Enter Password");
        return false;
      }
     
      let user;
      try {
        user = await Auth.signIn(username, password);
      } catch (err) {
        return setError("Invalid credentials. Please try again.");
      }

      const handled = await routeChallenge(user);
      if (handled) return;

      await enforceMfaSetup(user);

    } catch (error) {
      if (["UserNotFoundException", "NotAuthorizedException"].includes(error.code)) {
        setError("Invalid Username or Password");
        return false;
      }
      console.log("SignIn Error - ", error);
    }
  }

  const enforceMfaSetup = async (user) => {
    try {
      const prefer = await Auth.getPreferredMFA(user, { bypassCache: true });
      if (prefer === "SOFTWARE_TOKEN_MFA") {
        setMfaStep("none");
        handleLoginSuccess(user);
        return;
      }
    } catch (err) {
      console.warn("Could not read MFA preference, forcing setup:", err);
    }
    const secret = await Auth.setupTOTP(user);
    setMfaUser(user);
    setTotpSecret(secret);
    setTotpUri(buildTotpUri(secret));
    setMfaStep("setup");
  };

  const buildTotpUri = (secret) => {
    const issuer = "McDonalds SSP";
    return `otpauth://totp/${encodeURIComponent(issuer + ":" + username)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
  };

  const routeChallenge = async (user) => {
    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      setMfaUser(user);
      setMfaStep("newPassword");
      return true;
    }
    if (user.challengeName === "SOFTWARE_TOKEN_MFA") {
      setMfaUser(user);
      setMfaStep("verify");
      return true;
    }
    if (user.challengeName === "MFA_SETUP") {
      setMfaUser(user);
      const secret = await Auth.setupTOTP(user);
      setTotpSecret(secret);
      setTotpUri(buildTotpUri(secret));
      setMfaStep("setup");
      return true;
    }
    return false;
  };

  const onClickCompleteNewPassword = async () => {
    if (!newPassword) {
      return setError("Please enter a new password.");
    }
    if (newPassword !== confirmPassword) {
      return setError("Passwords do not match.");
    }
    try {
      const requiredAttributes = {};
      const missing = mfaUser.challengeParam?.requiredAttributes || [];
      if (missing.includes("preferred_username")) {
        requiredAttributes.preferred_username = username;
      }
      const user = await Auth.completeNewPassword(mfaUser, newPassword, requiredAttributes);
      setNewPassword("");
      setConfirmPassword("");
      const handled = await routeChallenge(user);
      if (!handled) {
        await enforceMfaSetup(user);
      }
    } catch (err) {
      setError(err.message || "Could not set the new password. Please try again.");
    }
  };

  const onClickVerifyTotpSetup = async () => {
    if (!mfaCode) {
      return setError("Please enter the code from your authenticator app.");
    }
    try {
      await Auth.verifyTotpToken(mfaUser, mfaCode);
      await Auth.setPreferredMFA(mfaUser, "TOTP");
      setMfaStep("none");
      setMfaCode("");
      handleLoginSuccess(mfaUser);
    } catch (err) {
      setError("Invalid code. Please try again.");
    }
  };

  const onClickConfirmMfa = async () => {
    if (!mfaCode) {
      return setError("Please enter your authentication code.");
    }
    try {
      const user = await Auth.confirmSignIn(mfaUser, mfaCode, "SOFTWARE_TOKEN_MFA");
      setMfaStep("none");
      setMfaCode("");
      handleLoginSuccess(user);
    } catch (err) {
      setError("Invalid code. Please try again.");
    }
  };

  const onClickSSOLogin = async () => {
    try {
      await Auth.federatedSignIn({
        provider: CognitoHostedUIIdentityProvider.Cognito
      });
    } catch (error) {
      console.error("SSO Sign In Error - ", error);
      setError("An error occurred during SSO login. Please try again.");
    }
  }

  return (
    <div className="login-wrapper">
      <LoadingSpinner show={isLoading} />

      <div className="login-logo flex-grow-1">
        <div className="signin-box text-center">
          <h5 className="text-dark mb-3">Welcome!</h5>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {message && (
            <div className="alert alert-success" role="alert">
              {message}
            </div>
          )}
          {mfaStep === "none" && (
          <div>
            <div className="form-group">
              <input
                onChange={onChangeUsername}
                value={username}
                autoComplete="off"
                className="form-control"
                placeholder="Enter Username"
              />
            </div>
            <div className="form-group">
              <input
                onChange={onChangePassword}
                value={password}
                type="password"
                className="form-control"
                placeholder="Enter Password"
              />
            </div>
            <button type='button' className="signin-btn" onClick={onClickLogin}>Login via Local Account</button>
            <hr/>
            <button type='button' className="signin-btn" onClick={onClickSSOLogin}>Login via SSO</button>
          </div>
          )}

          {mfaStep === "newPassword" && (
          <div>
            <p className="text-dark mb-2">Set a new password to activate your account.</p>
            <div className="form-group">
              <input
                onChange={(e) => { setError(""); setNewPassword(e.target.value); }}
                value={newPassword}
                type="password"
                className="form-control"
                placeholder="New password"
              />
            </div>
            <div className="form-group">
              <input
                onChange={(e) => { setError(""); setConfirmPassword(e.target.value); }}
                value={confirmPassword}
                type="password"
                className="form-control"
                placeholder="Confirm new password"
              />
            </div>
            <button type='button' className="signin-btn" onClick={onClickCompleteNewPassword}>Set Password and Continue</button>
          </div>
          )}

          {mfaStep === "setup" && (
          <div>
            <p className="text-dark mb-2">Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator), then enter the 6-digit code.</p>
            {totpUri && (
              <div className="form-group" style={{ display: "flex", justifyContent: "center" }}>
                <QRCodeSVG value={totpUri} size={180} includeMargin={true} />
              </div>
            )}
            <details className="mb-2">
              <summary style={{ cursor: "pointer" }}>Can't scan? Enter this key manually</summary>
              <code style={{ wordBreak: "break-all" }}>{totpSecret}</code>
            </details>
            <div className="form-group">
              <input
                onChange={(e) => { setError(""); setMfaCode(e.target.value); }}
                value={mfaCode}
                autoComplete="one-time-code"
                inputMode="numeric"
                className="form-control"
                placeholder="Enter 6-digit code"
              />
            </div>
            <button type='button' className="signin-btn" onClick={onClickVerifyTotpSetup}>Verify and Continue</button>
          </div>
          )}

          {mfaStep === "verify" && (
          <div>
            <p className="text-dark mb-2">Enter the 6-digit code from your authenticator app.</p>
            <div className="form-group">
              <input
                onChange={(e) => { setError(""); setMfaCode(e.target.value); }}
                value={mfaCode}
                autoComplete="one-time-code"
                inputMode="numeric"
                className="form-control"
                placeholder="Enter 6-digit code"
              />
            </div>
            <button type='button' className="signin-btn" onClick={onClickConfirmMfa}>Verify</button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
